/**
 * AnalyticsCharts
 *
 * Charts section of the analytics dashboard.
 * Includes MRR, Customer Growth, Plan Distribution, and Trial Activity charts.
 */
import React from 'react';
import { Info, TrendingUp, Activity, Users } from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip
} from 'recharts';
import AnalyticsTooltip from './AnalyticsTooltip';

const AnalyticsCharts = ({ 
  analyticsData, 
  formatDate, 
  formatDateLong, 
  formatCurrency, 
  formatNumber,
  getDaysForPeriod,
  selectedPeriod,
  colors,
  pieColors
}) => {
  return (
    <div className="space-y-8">
      {/* Revenue Trends - Simplified Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Recurring Revenue (MRR) Trend */}
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Monthly Recurring Revenue
            </h3>
            <AnalyticsTooltip content="Your predictable monthly revenue from active subscriptions. Shows growth over time." id="mrr-trend-tooltip">
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </AnalyticsTooltip>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analyticsData.revenueTrends || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                stroke="#6B7280"
                fontSize={12}
                interval="preserveStartEnd"
                minTickGap={30}
              />
              <YAxis 
                stroke="#6B7280"
                tickFormatter={(value) => `$${(value/1000).toFixed(1)}k`}
                fontSize={12}
              />
              <RechartsTooltip 
                labelFormatter={(value) => formatDateLong(value)}
                formatter={(value) => [formatCurrency(value), 'MRR']}
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="mrr" 
                stroke="#3B82F6" 
                fill="#3B82F6"
                fillOpacity={0.2}
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>Current MRR: <strong className="text-gray-900 dark:text-white">{formatCurrency(analyticsData.summary?.currentMRR || 0)}</strong></span>
            <span className="text-emerald-600 dark:text-emerald-400">+{formatNumber((analyticsData.summary?.mrrGrowthRate || 0), 1)}% growth</span>
          </div>
        </div>

        {/* New Paying Customers */}
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              New Paying Customers
            </h3>
            <AnalyticsTooltip content="Number of new paying customers added each day (first payment or subscription start)." id="new-paying-customers-tooltip">
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </AnalyticsTooltip>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            {(() => {
              const ct = analyticsData.customerTrends || [];
              const data = ct.map(d => ({ date: d.date, newCustomers: d.newCustomers || 0 }));
              return (
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="#6B7280"
                    fontSize={12}
                    interval="preserveStartEnd"
                    minTickGap={30}
                  />
                  <YAxis stroke="#6B7280" fontSize={12} />
                  <RechartsTooltip 
                    labelFormatter={(value) => formatDateLong(value)}
                    formatter={(value) => [value, 'New Paying Customers']}
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Bar dataKey="newCustomers" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              );
            })()}
          </ResponsiveContainer>
          {(() => {
            const ct = analyticsData.customerTrends || [];
            const total = ct.reduce((s, d) => s + (d.newCustomers || 0), 0);
            const avg = total / Math.max(getDaysForPeriod(selectedPeriod), 1);
            return (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>Total New Customers: <strong className="text-gray-900 dark:text-white">{total.toLocaleString()}</strong></span>
                <span>Avg Per Day: <strong className="text-gray-900 dark:text-white">{avg.toFixed(1)}</strong></span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Customer Growth & Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Acquisition Trend */}
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Total Customer Growth
            </h3>
            <AnalyticsTooltip content="Track your total paying customer count over time, including all existing customers. Shows the cumulative growth of your customer base, not just daily new acquisitions." id="acquisition-tooltip">
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </AnalyticsTooltip>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={analyticsData.customerTrends || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                stroke="#6B7280"
                fontSize={12}
                interval="preserveStartEnd"
                minTickGap={30}
              />
              <YAxis 
                stroke="#6B7280"
                fontSize={12}
              />
              <RechartsTooltip 
                labelFormatter={(value) => formatDateLong(value)}
                formatter={(value, name) => [value, name === 'totalCustomers' ? 'Total Customers' : name]}
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="totalCustomers" 
                stroke="#3B82F6" 
                fill="#3B82F6"
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>Current Total Customers: <strong className="text-gray-900 dark:text-white">{analyticsData.summary?.payingCustomers || 0}</strong></span>
            <span>Recent Activity: <strong className="text-emerald-600 dark:text-emerald-400">Growing</strong></span>
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Plan Distribution
            </h3>
            <AnalyticsTooltip content="Current distribution of users across different plan types" id="plans-tooltip">
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </AnalyticsTooltip>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={analyticsData.planDistribution || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {(analyticsData.planDistribution || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                ))}
              </Pie>
              <RechartsTooltip 
                formatter={(value, name) => [value, 'Users']}
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Trial Activity */}
      <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Trial Activity
          </h3>
          <AnalyticsTooltip content="Track trial starts over time to see acquisition patterns and seasonal trends." id="trial-activity-tooltip">
            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </AnalyticsTooltip>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={analyticsData.conversionTrends || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke="#6B7280"
              fontSize={11}
              interval="preserveStartEnd"
            />
            <YAxis stroke="#6B7280" fontSize={11} />
            <RechartsTooltip 
              labelFormatter={(value) => formatDateLong(value)}
              formatter={(value) => [value, 'Trial Starts']}
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#F9FAFB'
              }}
            />
            <Bar 
              dataKey="trialStarts" 
              fill="#F59E0B"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Total Trials: <strong className="text-gray-900 dark:text-white">{analyticsData.summary?.trialUsers || 0}</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCharts;


